import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AssessmentService } from '../../assessments/assessment.service';
import { ActivatedRoute } from '@angular/router';
import { DashboardService } from '../../dashboard.service';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/app/services/loader.service';
import { toasterMessages } from '@messages/*';
import * as ace from "ace-builds";
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { RegenerateQuestionDialogComponent } from 'src/app/components/regenerate-question-dialog/regenerate-question-dialog.component';
import { RegenerateFlowService } from 'src/app/services/regenerate-flow.service';
import { MatMenu } from '@angular/material/menu';
import { configureAceSession } from 'src/app/shared/ace-mode-utils';

@Component({
  selector: 'app-cqs-list',
  templateUrl: './cqs-list.component.html',
  styleUrls: ['./cqs-list.component.scss']
})
export class CqsListComponent implements OnInit {

  // Input/Output properties
  @Input() cqsInfo: any;
  @Input() fromView: any;
  @Input() showCheckboxes = false;
  @Output() removeSelectedCQ = new EventEmitter<string>();
  @Output() selectedCQ = new EventEmitter<any>();
  @Output() cqScroll = new EventEmitter<string>();
  @Output() bulkActionDone = new EventEmitter<void>();
  @Output() cqCountChanged = new EventEmitter<number>();

  // ViewChild references
  @ViewChild("verifyCodeEditor") private verifyCodeEditor: ElementRef<HTMLElement>;
  @ViewChild("answerCodeEditor") private answerCodeEditor: ElementRef<HTMLElement>;
  @ViewChild('cqMenu') cqMenu!: MatMenu;

  // Private properties
  private subscriptions: Subscription[] = [];
  private assessmentId: string = '';
  private editAssessmentId: string = '';
  private selectedCqUndoTimeout: any;


  // Public properties
  undoSelectedItem: any = { message: 'Question has been selected', duration: 5 };
  errorMessage: string = '';
  generateSuccess: boolean = false;
  displayText: string = 'Re-Generating Question...';

  menuItem: any;

  // Editor configurations
  editorConfig: AngularEditorConfig = {
    height: 'auto',
    minHeight: '0',
    width: 'auto',
    minWidth: '0',
    showToolbar: false,
    editable: false,
    spellcheck: true
  };

  editorEditConfig: AngularEditorConfig = {
    height: 'auto',
    minHeight: '0',
    width: 'auto',
    minWidth: '0',
    editable: true,
    spellcheck: true,
    enableToolbar: true,
    showToolbar: true
  };

  // Ace editor instances
  verifyCodeAceEditor: any;
  answerCodeAceEditor: any;
  sourceCodefindIndex = -1;
  btnRunCodeTxt = 'Run Code';

  constructor(
    private assessmentService: AssessmentService,
    private activatedRoute: ActivatedRoute,
    private dashboardService: DashboardService,
    private toastr: ToastrService,
    private loaderService: LoaderService,
    private dialog: MatDialog,
    private regenerateFlow: RegenerateFlowService
  ) {
    this.editAssessmentId = this.activatedRoute.snapshot.paramMap.get('id');
  }

  ngOnInit(): void {
    this.assessmentId = this.cqsInfo.assessmentId || this.editAssessmentId;
    this.initializeQuestionStates();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  // ==================== INITIALIZATION METHODS ====================

  private initializeQuestionStates(): void {
    this.fromView = this.fromView || '';
    if (this.cqsInfo?.questions) {
      this.cqsInfo.questions.forEach(question => {
        if (question._selected === undefined) question._selected = false;
        if (question.expanded === undefined) question.expanded = false;
      });
    }
  }

  // ==================== MENU & UI INTERACTION METHODS ====================

  setMenuItem(item: any): void {
    this.menuItem = item;
  }

  toggleExpand(item: any): void {
    item.expanded = !item.expanded;
    const idx = this.cqsInfo?.questions?.indexOf(item) || 0;
    if (item.expanded) {
      // Use robust DOM-ready check to initialize editors reliably across sections
      this.ensureEditorsInitialized(item, idx, 0);
    } else {
      this.onPanelClose(item);
    }
  }

  onSelectChange(item: any, event: any): void {
    item._selected = event.checked;
  }

  hasSelectedQuestions(): boolean {
    return this.cqsInfo?.questions?.some(q => q._selected);
  }

  // ==================== MENU ACTION METHODS ====================

  onEditClick(item: any): void {
    this.cqsInfo.questions.forEach(q => q.question_edit_clicked = false);
    this.onQuestionEditClick(item, this.cqsInfo?.questions?.indexOf(item) || 0);
    this.closeMenu();
  }

  onDuplicate(item: any): void {
    const payload: any = { id: item._id };
    if (this.cqsInfo?.category === 'PREVIOUSLYGENERATED') {
      // Library copy: do not link to assessment; stays under Previously Generated
    } else if (this.assessmentId) {
      // Generated section copy: keep in Generated for this assessment
      payload.assessmentId = this.assessmentId;
      payload.status = 'GENERATED';
    }
    this.dashboardService.CopyQuestion(payload).subscribe({
      next: (res) => {
        if (res && res._id) {
          this.cqsInfo.questions.unshift(res);
          this.cqsInfo.totalQuestions += 1;
          this.cqCountChanged.emit(this.cqsInfo.totalQuestions);
          this.toastr.success('Question copied successfully!', 'Success', { timeOut: 2500 });
          this.closeMenu();
        } else {
          this.toastr.error('Failed to copy question.', 'Error', { timeOut: 2500 });
        }
      },
      error: (err) => {
        this.toastr.error('Failed to copy question.', 'Error', { timeOut: 2500 });
        this.closeMenu();
      }
    });
  }

  onDelete(item: any): void {
    this.confirmDeleteSelected(item);
  }

  confirmDeleteSelected(item: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'delete-dialog-container',
      data: {
        danger: true,
        messageBody: `Are you sure you want to delete this question? This action cannot be undone.`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        this.dashboardService.deleteQuestion(item._id).subscribe({
          next: () => {
            this.cqsInfo.questions = this.cqsInfo.questions.filter(q => q._id !== item._id);
            this.cqsInfo.totalQuestions -= 1;
            this.cqCountChanged.emit(this.cqsInfo.totalQuestions);
            this.toastr.success('Question deleted successfully!', 'Success', { timeOut: 2500 });
            this.closeMenu();
          },
          error: (err) => {
            const msg = err?.error?.errors?.[0]?.error || 'Failed to delete question.';
            this.toastr.error(msg, 'Delete Error', { timeOut: 3000 });
            this.closeMenu();
          }
        });
      }
    });
  }

  onBulkDelete(): void {
    const selectedQuestions = this.cqsInfo?.questions?.filter(q => q._selected) || [];

    if (selectedQuestions.length === 0) {
      this.toastr.warning('No questions selected for deletion.', 'Warning', { timeOut: 2500 });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'delete-dialog-container',
      data: {
        danger: true,
        messageBody: `Are you sure you want to delete ${selectedQuestions.length} selected question(s)? This action cannot be undone.`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        const questionIds = selectedQuestions.map(q => q._id);
        this.dashboardService.deleteMultipleQuestions(questionIds).subscribe({
          next: (res) => {
            this.cqsInfo.questions = this.cqsInfo.questions.filter(q => !q._selected);
            this.cqsInfo.totalQuestions -= selectedQuestions.length;
            this.cqCountChanged.emit(this.cqsInfo.totalQuestions);
            this.bulkActionDone.emit();
            if (res && res.message) {
              this.toastr.info(res.message, 'Bulk Delete', { timeOut: 3500 });
            } else {
              this.toastr.success('Selected questions deleted successfully!', 'Success', { timeOut: 2500 });
            }
          },
          error: (err) => {
            this.toastr.error('Failed to delete selected questions.', 'Error', { timeOut: 2500 });
          }
        });
      }
    });
  }

  // ==================== SELECTION METHODS ====================

  addCqToAssessment(item: any): void {
    item.selected = true;
    item.edit_clicked = false;
    item.displayText = 'Adding question...';

    const data = {
      status: "SELECTED",
      assessmentId: this.assessmentId
    };

    this.subscriptions.push(
      this.assessmentService.selectOrUndoQuestionToAssessment(item._id, data).subscribe({
        next: (res) => {
          item.displayText = '';
          const serverItem = (res as any) && (res as any)._id
            ? { ...item, ...(res as any), sourceQuestionId: item._id, originQuestion: item }
            : item;
          this.selectedCqUndoTimeout = setTimeout(() => {
            serverItem.selected = false;
            serverItem.status = "SELECTED";
            this.selectedCQ.emit(serverItem);
          }, this.undoSelectedItem.duration * 1000);
        },
        error: (err) => {
          this.errorMessage = err?.error?.errors[0]?.error || 'Something went wrong please try again!';
          item.error = true;
          setTimeout(() => {
            item.selected = false;
            item.error = false;
          }, 3000);
        }
      })
    );
  }

  undoSelectedIQuestion(item: any): void {
    clearTimeout(this.selectedCqUndoTimeout);
    item.selected = true;
    item.displayText = 'The selected question is being undone...';

    const data = {
      status: "GENERATED",
      assessmentId: this.assessmentId
    };

    this.subscriptions.push(
      this.assessmentService.selectOrUndoQuestionToAssessment(item._id, data).subscribe({
        next: (res) => {
          item.displayText = '';
          item.selected = false;
        },
        error: (err) => {
          item.error = true;
          setTimeout(() => {
            item.selected = false;
            item.error = false;
          }, 3000);
        }
      })
    );
  }

  deselectQuestion(item: any): void {
    item.status = 'GENERATED';
    item.selected = false;
    this.cqCountChanged.emit(this.cqsInfo.questions.length);
  }



  // ==================== DELETE METHODS ====================



  // ==================== EDIT METHODS ====================

  onQuestionEditClick(editItem: any, cqIdx: number): void {
    this.cqsInfo.questions.forEach(q => q.question_edit_clicked = false);
    editItem.question_edit_clicked = true;
    editItem.temp_questionText = this.formatQuestionText(editItem.questionText);
    editItem._original_questionText = editItem.questionText;

    // Auto-expand the question when editing
    if (!editItem.expanded) {
      editItem.expanded = true;
      this.ensureEditorsInitialized(editItem, cqIdx, 0);
    }
  }

  onAnswerEditClick(editItem: any, cqIdx: number): void {
    editItem.answer_edit_clicked = true;
    const langIdx = editItem.testCaseRunLangId
      ? Math.max(0, editItem.questionSignatures.findIndex((a: any) => a.functionSignature.language_id === editItem.testCaseRunLangId))
      : 0;
    this.updateEditorContent(editItem, langIdx, cqIdx);
    this.initializeCodeEditorForEdit(`coding-ans-ace-editor-${cqIdx}${this.fromView}`);
  }

  onInitialEditClick(editItem: any, cqIdx: number): void {
    editItem.initial_edit_clicked = true;
    const langIdx = editItem.testCaseRunLangId
      ? Math.max(0, editItem.questionSignatures.findIndex((a: any) => a.functionSignature.language_id === editItem.testCaseRunLangId))
      : 0;
    this.updateEditorContent(editItem, langIdx, cqIdx);
    this.initializeCodeEditorForEdit(`preview-ace-editor-${cqIdx}${this.fromView}`);
  }

  onQuestionEditCancelBtnClick(editedItem: any): void {
    if (editedItem?.question_edit_clicked) {
      editedItem.question_edit_clicked = false;
      editedItem.questionText = editedItem._original_questionText;
      editedItem.temp_questionText = this.formatQuestionText(editedItem.questionText);
      // Re-initialize editors to restore content after exiting edit mode
      const idx = this.cqsInfo?.questions?.findIndex((q: any) => q._id === editedItem._id) || 0;
      const prevLangId = editedItem.testCaseRunLangId;
      const langIdx = typeof prevLangId !== 'undefined'
        ? Math.max(0, editedItem.questionSignatures.findIndex((a: any) => a.functionSignature.language_id === prevLangId))
        : 0;
      this.ensureEditorsInitialized(editedItem, idx, langIdx);
    }
  }

  onAnswerEditCancelBtnClick(editedItem: any, i: number): void {
    if (editedItem?.answer_edit_clicked) {
      editedItem.answer_edit_clicked = false;
      // Revert answer editor content from stored source
      const langIdx = editedItem.testCaseRunLangId
        ? Math.max(0, editedItem.questionSignatures.findIndex((a: any) => a.functionSignature.language_id === editedItem.testCaseRunLangId))
        : 0;
      this.updateEditorContent(editedItem, langIdx, i);
      this.setEditorReadOnly(`coding-ans-ace-editor-${i}${this.fromView}`, true);
      this.updateEditorHeights(i);

      const idx = this.cqsInfo?.questions?.findIndex((q: any) => q._id === editedItem._id) || 0;
      const cardEl = document.getElementById(`cq-card-${idx}${this.fromView}`);
      if (cardEl) {
        this.scrollIntoViewWithOffset(cardEl as HTMLElement, 65);
      }
    }
  }

  onInitialEditCancelBtnClick(editedItem: any, i: number): void {
    if (editedItem?.initial_edit_clicked) {
      editedItem.initial_edit_clicked = false;
      // Reset initial editor content from stored source
      const langIdx = editedItem.testCaseRunLangId
        ? Math.max(0, editedItem.questionSignatures.findIndex((a: any) => a.functionSignature.language_id === editedItem.testCaseRunLangId))
        : 0;
      this.updateEditorContent(editedItem, langIdx, i);
      this.setEditorReadOnly(`preview-ace-editor-${i}${this.fromView}`, true);
      this.updateEditorHeights(i);
    }
  }

  /** Cancel for either Initial Code or Answer when one of them is active */
  onCombinedEditCancel(item: any, i: number): void {
    if (item.question_edit_clicked) {
      this.onQuestionEditCancelBtnClick(item);
    }
    if (item.initial_edit_clicked) {
      this.onInitialEditCancelBtnClick(item, i);
    }
    if (item.answer_edit_clicked) {
      this.onAnswerEditCancelBtnClick(item, i);
    }
  }

  onEditDone(question: any, i: number): void {
    this.loaderService.show('Updating coding question');
    const payload: any = {
      questionText: question?.temp_questionText?.replace(/&#10;/g, '\n'),
    };
    delete question.temp_questionText;

    const verifyEditor = ace.edit(`preview-ace-editor-${i}${this.fromView}`);
    const answerEditor = ace.edit(`coding-ans-ace-editor-${i}${this.fromView}`);
    const sourceCodeObj = question.questionSignatures.find((a: any) => a.functionSignature.language_id === question.testCaseRunLangId);
    if (sourceCodeObj) {
      payload.functionSignature = {
        language_id: sourceCodeObj.functionSignature.language_id,
        initial_code: this.convertEscapedNewlines(verifyEditor?.getValue()),
        source_code: this.convertEscapedNewlines(answerEditor?.getValue())
      };
    }

    this.subscriptions.push(
      this.dashboardService.updateQuestion(payload, question._id).subscribe({
        next: (response) => {
          this.loaderService.hide();
          const selectedIndex = this.cqsInfo?.questions?.findIndex(x => x._id === question._id);
          this.cqsInfo.questions[selectedIndex] = response;
          question.question_edit_clicked = false;
          question.answer_edit_clicked = false;
          question.initial_edit_clicked = false;
          // Re-initialize editors with previously selected language to avoid blank editors
          const prevLangId = (question as any).testCaseRunLangId;
          let langIdx = typeof prevLangId !== 'undefined'
            ? (response?.questionSignatures || []).findIndex((a: any) => a.functionSignature.language_id === prevLangId)
            : 0;
          if (langIdx < 0) langIdx = 0;
          // Scroll back to the edited question card to keep user context
          const cardEl = document.getElementById(`cq-card-${selectedIndex}${this.fromView}`);
          if (cardEl) {
            this.scrollIntoViewWithOffset(cardEl as HTMLElement, 65);
          }
          // If editors already exist for this row, update content directly; otherwise ensure init
          const previewEl = document.getElementById(`preview-ace-editor-${selectedIndex}${this.fromView}`);
          const answerEl = document.getElementById(`coding-ans-ace-editor-${selectedIndex}${this.fromView}`);
          if (previewEl && answerEl) {
            this.updateEditorContent(this.cqsInfo.questions[selectedIndex], langIdx, selectedIndex);
            this.setEditorReadOnly(`preview-ace-editor-${selectedIndex}${this.fromView}`, true);
            this.setEditorReadOnly(`coding-ans-ace-editor-${selectedIndex}${this.fromView}`, true);
            this.updateEditorHeights(selectedIndex);
          } else {
            this.ensureEditorsInitialized(this.cqsInfo.questions[selectedIndex], selectedIndex, langIdx);
          }
          this.toastr.success(toasterMessages.questionCQUpdated, 'Question updated', { timeOut: 2500 });
          this.closeMenu();
        },
        error: (err) => {
          this.loaderService.hide();
          question.questionText = question._original_questionText;
          question.temp_questionText = question.questionText;
          this.toastr.error(err?.error?.errors?.[0]?.error || 'Something went wrong!', 'Error', { timeOut: 2500 });
        }
      })
    );
  }

  // ==================== CODE EDITOR METHODS ====================

  onPanelOpen(cq: any, cqIdx: number, langIdx = 0): void {
    cq.expanded = true;
    if (langIdx < 0) langIdx = 0;
    this.initializeAceEditors(cq, cqIdx, langIdx);
    // Preserve current selection if available; otherwise set defaults
    if (!cq.testCaseRunLangId) {
      this.setupQuestionDefaults(cq);
      // Ensure editors reflect default language
      this.updateEditorContent(cq, 0, cqIdx);
    } else {
      const currIdx = cq.questionSignatures.findIndex((a: any) => a.functionSignature.language_id === cq.testCaseRunLangId);
      if (currIdx > -1) {
        this.updateEditorContent(cq, currIdx, cqIdx);
      }
    }
  }

  onPanelClose(item: any): void {
    item.expanded = false;
    if (item?.answer_edit_clicked) {
      item.answer_edit_clicked = false;
    }
    if (item?.question_edit_clicked) {
      item.question_edit_clicked = false;
    }
  }

  selectedCodingLang(lang: any, item: any, cqIdx: number): void {
    item.testCaseRunLangId = lang.value;
    // Clear any previously displayed test results when language changes
    if (Array.isArray(item?.questionSignatures)) {
      item.questionSignatures.forEach((sig: any) => {
        if (sig && sig.testResults) {
          delete sig.testResults;
        }
      });
    }
    // Hide the results panel until user runs tests again
    this.sourceCodefindIndex = -1;

    const sourceCodefindIndex = item.questionSignatures.findIndex((a: any) => a.functionSignature.language_id === item.testCaseRunLangId);

    this.updateEditorContent(item, sourceCodefindIndex, cqIdx);
    this.updateEditorHeights(cqIdx);
  }

  runCode(item: any): void {
    this.btnRunCodeTxt = 'Code Running';
    this.sourceCodefindIndex = item.questionSignatures.findIndex((a: any) => a.functionSignature.language_id === item.testCaseRunLangId);

    const reqData = {
      language_id: item.testCaseRunLangId,
      source_code: this.convertEscapedNewlines(
        item.questionSignatures[this.sourceCodefindIndex]?.functionSignature?.source_code,
        item?.questionSignatures[this.sourceCodefindIndex]?.functionSignature?.code_language,
        item?.questionSignatures[this.sourceCodefindIndex]?.functionSignature?.language_id
      )
    };

    this.dashboardService.runTests(reqData).subscribe({
      next: (res) => {
        item.questionSignatures[this.sourceCodefindIndex].testResults = res.output || res.errorMessage;
        this.btnRunCodeTxt = 'Run Code';
      },
      error: (error) => {
        this.btnRunCodeTxt = 'Run Code';
        this.toastr.error(error?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
      }
    });
  }

  // ==================== REGENERATE METHODS ====================

  // Removed old GET-based re-generate method; dialog uses POST with optional hints

  openRegenerateDialog(question: any): void {
    if (this.cqMenu && this.cqMenu.closed) {
      this.cqMenu.closed.emit();
    }
    this.subscriptions.push(
      this.regenerateFlow.ask('CODING', question.additionalPrompts || []).subscribe((result: any) => {
        if (!result || result === 'cancel') { return; }
        question.regenerate = true;
        this.generateSuccess = false;
        this.displayText = 'Re-Generating Question...';
        const headers = { 'skip-loader': 'true' };
        this.dashboardService.reGenerateQuestion(question._id, headers, result.additionalPrompts || []).subscribe({
          next: (response) => {
            const selectedIndex = this.cqsInfo?.questions?.findIndex(x => x._id === question._id);
            this.displayText = 'Successfully Re-Generated Question';
            this.generateSuccess = true;
            this.cqsInfo.questions[selectedIndex] = response.question;
            question.regenerate = false;
            this.closeMenu();
            // transient highlight to indicate which item got regenerated
            const updated = this.cqsInfo.questions[selectedIndex];
            if (updated) {
              updated._highlight = true;
              setTimeout(() => { updated._highlight = false; }, 4000);
            }
          },
          error: (err) => {
            this.toastr.error(err?.error?.errors[0]?.error, 'Error', { timeOut: 2500 });
            this.generateSuccess = false;
            question.regenerate = false;
            this.displayText = '';
            this.closeMenu();
          }
        });
      })
    );
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Scroll the given element into view accounting for a fixed header height.
   * Uses window scroll to avoid native scrollIntoView covering the target under the header.
   */
  private scrollIntoViewWithOffset(target: HTMLElement, offsetPx: number = 65): void {
    try {
      const rect = target.getBoundingClientRect();
      const absoluteTop = rect.top + window.pageYOffset;
      const top = Math.max(absoluteTop - offsetPx, 0);
      window.scrollTo({ top, behavior: 'smooth' });
    } catch {
      // Fallback
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onOeqScroll(): void {
    this.cqScroll.emit();
  }

  onCqScroll(): void {
    this.cqScroll.emit();
  }

  private closeMenu(): void {
    if (this.cqMenu && this.cqMenu.closed) {
      this.cqMenu.closed.emit();
    }
  }

  private initializeAceEditors(cq: any, cqIdx: number, langIdx: number): void {
    ace.config.set("fontSize", "14px");
    ace.config.set('basePath', 'https://unpkg.com/ace-builds@1.4.12/src-noconflict');

    // Initialize question editor
    const verifyCodeAceEditor = ace.edit(`preview-ace-editor-${cqIdx}${this.fromView}`);
    const initialCode =
      cq?.questionSignatures[langIdx]?.functionSignature?.initial_code ??
      cq?.questionSignatures[langIdx]?.initialCode ??
      '';
    verifyCodeAceEditor?.session?.setValue(this.convertEscapedNewlines(initialCode, cq?.questionSignatures[langIdx]?.functionSignature?.code_language, cq?.questionSignatures[langIdx]?.functionSignature?.language_id));
    verifyCodeAceEditor?.setTheme('ace/theme/solarized_dark');
    configureAceSession(verifyCodeAceEditor.session, cq?.questionSignatures[langIdx]?.functionSignature?.code_language);
    verifyCodeAceEditor?.setReadOnly(true);
    verifyCodeAceEditor.setOptions({ showLineNumbers: false });

    // Initialize answer editor
    const answerCodeAceEditor = ace.edit(`coding-ans-ace-editor-${cqIdx}${this.fromView}`);
    const sourceCode =
      cq?.questionSignatures[langIdx]?.functionSignature?.source_code ??
      '';
    answerCodeAceEditor?.session?.setValue(this.convertEscapedNewlines(sourceCode, cq?.questionSignatures[langIdx]?.functionSignature?.code_language, cq?.questionSignatures[langIdx]?.functionSignature?.language_id));
    answerCodeAceEditor?.setTheme('ace/theme/solarized_dark');
    configureAceSession(answerCodeAceEditor.session, cq?.questionSignatures[langIdx]?.functionSignature?.code_language);
    answerCodeAceEditor?.setReadOnly(true);

    this.updateEditorHeights(cqIdx);
  }

  /**
   * Safely (re)initialize editors after DOM state changes.
   * Retries a few times until the target element exists to avoid race conditions.
   */
  private ensureEditorsInitialized(item: any, idx: number, langIdx: number = 0, attempt: number = 0): void {
    const maxAttempts = 8;
    const previewId = `preview-ace-editor-${idx}${this.fromView}`;
    const answerId = `coding-ans-ace-editor-${idx}${this.fromView}`;
    const previewEl = document.getElementById(previewId);
    const answerEl = document.getElementById(answerId);
    if (previewEl && answerEl) {
      this.onPanelOpen(item, idx, langIdx);
      return;
    }
    if (attempt >= maxAttempts) {
      // Give up silently to avoid console noise in prod; user can retry
      return;
    }
    setTimeout(() => this.ensureEditorsInitialized(item, idx, langIdx, attempt + 1), 80);
  }

  private setupQuestionDefaults(cq: any): void {
    cq.selectedLang = cq.questionSignatures[0].functionSignature.language_id;
    cq.testCaseRunLangId = cq.selectedLang;
  }

  private updateEditorContent(item: any, sourceCodefindIndex: number, cqIdx: number): void {
    const initialCode =
      item?.questionSignatures[sourceCodefindIndex]?.functionSignature?.initial_code ??
      item?.questionSignatures[sourceCodefindIndex]?.initialCode ??
      '';
    const sourceCode =
      item?.questionSignatures[sourceCodefindIndex]?.functionSignature?.source_code ??
      '';

    const verifyCodeAceEditor = ace.edit(`preview-ace-editor-${cqIdx}${this.fromView}`);
    verifyCodeAceEditor?.session?.setValue(this.convertEscapedNewlines(initialCode, item?.questionSignatures[sourceCodefindIndex]?.functionSignature?.code_language, item?.questionSignatures[sourceCodefindIndex]?.functionSignature?.language_id));
    configureAceSession(verifyCodeAceEditor.session, item?.questionSignatures[sourceCodefindIndex]?.functionSignature?.code_language);

    const answerCodeAceEditor = ace.edit(`coding-ans-ace-editor-${cqIdx}${this.fromView}`);
    answerCodeAceEditor?.session?.setValue(this.convertEscapedNewlines(sourceCode, item?.questionSignatures[sourceCodefindIndex]?.functionSignature?.code_language, item?.questionSignatures[sourceCodefindIndex]?.functionSignature?.language_id));
    configureAceSession(answerCodeAceEditor.session, item?.questionSignatures[sourceCodefindIndex]?.functionSignature?.code_language);
  }

  /**
   * Convert escaped newlines (\n as text) to actual newlines
   */
  private convertEscapedNewlines(code: string, codeLanguage?: string, languageId?: number): string {
    if (!code) return '';
    const lang = String(codeLanguage || '').toLowerCase();
    const isCandCpp = [50, 54].includes(Number(languageId)) || /\bc\+\+|cpp\b|^c$/.test(lang);
    if (isCandCpp) return code;
    return code.replace(/\\n/g, '\n');
  }

  private updateEditorHeights(idx: number): void {
    this.showPreviewFullQuestion(idx);
    this.showFullAnswer(idx);
  }

  private initializeCodeEditorForEdit(editorId: string): void {
    setTimeout(() => {
      const editor: any = ace.edit(editorId);
      if (editor) {
        editor.setReadOnly(false);
        editor.resize();
        editor.renderer.updateFull();
      }
    }, 100);
  }

  private setEditorReadOnly(editorId: string, isReadOnly: boolean): void {
    const editor: any = ace.edit(editorId);
    if (editor) {
      editor.setReadOnly(isReadOnly);
    }
  }

  private showPreviewFullQuestion(idx: number): void {
    const editor: any = ace.edit(`preview-ace-editor-${idx}${this.fromView}`);
    const height = editor.session.getScreenLength() * (editor.renderer.lineHeight || 14) + (editor.renderer.scrollBar?.getWidth?.() || editor.renderer.scrollBar?.width || 0);
    const element = document.getElementById('preview-ace-editor-' + idx + this.fromView);
    if (element) {
      element.style.height = height + 10 + 'px';
    }
    editor.resize();
  }

  private showFullAnswer(idx: number): void {
    const editor: any = ace.edit(`coding-ans-ace-editor-${idx}${this.fromView}`);
    const height = editor.session.getScreenLength() * (editor.renderer.lineHeight || 14) + (editor.renderer.scrollBar?.getWidth?.() || editor.renderer.scrollBar?.width || 0);
    const element = document.getElementById('coding-ans-ace-editor-' + idx + this.fromView);
    if (element) {
      element.style.height = height + 10 + 'px';
    }
    editor.resize();
  }

  formatQuestionText(text: string): string {
    if (!text) return '';
    let formatted = text;

    // Handle escaped newlines (common in JSON data)
    formatted = formatted.replace(/\\n/g, '\n');

    // 1. Handle Code Blocks: ```code``` -> Premium Code Box
    formatted = formatted.replace(/```[a-z]*\s*([\s\S]*?)```/g, (match, p1) => {
      const code = p1.trim();
      return `<div class="code-block-premium">${code}</div>`;
    });

    // 2. Format Headers (# or ##) -> Bold
    formatted = formatted.replace(/^#+\s*(.*)$/gm, '<b>$1</b>');

    // 3. Handle Markdown bold: **text** -> <b>text</b>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');

    // 4. Handle Inline Code: `code` -> <code>code</code>
    formatted = formatted.replace(/`(.*?)`/g, '<code>$1</code>');

    // 5. Handle Bullet Points: "- " or "* " at start of line -> Bullet
    formatted = formatted.replace(/^\s*[\-\*]\s+/gm, '&bull; ');

    // 6. Handle Newlines: \n -> <br>
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted;
  }
}
